import React, { useState } from 'react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../hooks/useTranslation';
import { Building2, Globe, Clock, Save } from 'lucide-react';

const OrgSettings = () => {
  const { currentOrg } = useAuth();
  const t = useTranslation();
  const [settings, setSettings] = useState({
    name: currentOrg?.name || '',
    domain: currentOrg?.domain || '',
    searchCadence: currentOrg?.settings?.searchCadence || '1year',
    timezone: currentOrg?.settings?.timezone || 'UTC'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSave = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await updateDoc(doc(db, 'organizations', currentOrg.id), {
        name: settings.name,
        settings: {
          searchCadence: settings.searchCadence,
          timezone: settings.timezone
        },
        updatedAt: serverTimestamp()
      });

      setSuccess(t('orgAdmin.organizationSettings.settingsUpdated'));
    } catch (err) {
      console.error('Error updating organization settings:', err);
      setError(t('orgAdmin.organizationSettings.settingsUpdateFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-2">
          {t('orgAdmin.organizationSettings.title')}
        </h2>
        <p className="text-slate-600 dark:text-slate-400">
          {t('orgAdmin.organizationSettings.subtitle')}
        </p>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4">
          <p className="text-red-700 dark:text-red-300">{error}</p>
        </div>
      )}

      {success && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg p-4">
          <p className="text-green-700 dark:text-green-300">{success}</p>
        </div>
      )}

      <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-6 space-y-6">
        {/* Organization Details */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-slate-800 dark:text-slate-100 flex items-center">
            <Building2 className="w-5 h-5 mr-2" />
            {t('orgAdmin.organizationSettings.organizationDetails')}
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                {t('orgAdmin.organizationSettings.organizationName')}
              </label>
              <input
                type="text"
                value={settings.name}
                onChange={(e) => setSettings(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={loading}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                {t('orgAdmin.organizationSettings.domain')}
              </label>
              <div className="flex items-center">
                <Globe className="w-4 h-4 text-slate-400 mr-2" />
                <span className="text-slate-600 dark:text-slate-400">{settings.domain}</span>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                {t('orgAdmin.organizationSettings.domainCannotChange')}
              </p>
            </div>
          </div>
        </div>

        {/* Search Settings */}
        <div className="space-y-4 border-t border-slate-200 dark:border-slate-700 pt-6">
          <h3 className="text-lg font-medium text-slate-800 dark:text-slate-100 flex items-center">
            <Clock className="w-5 h-5 mr-2" />
            {t('orgAdmin.organizationSettings.defaultSearchSettings')}
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                {t('orgAdmin.organizationSettings.defaultSearchCadence')}
              </label>
              <select
                value={settings.searchCadence}
                onChange={(e) => setSettings(prev => ({ ...prev, searchCadence: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={loading}
              >
                <option value="1year">{t('orgAdmin.organizationSettings.cadenceOptions.1year')}</option>
                <option value="6months">{t('orgAdmin.organizationSettings.cadenceOptions.6months')}</option>
                <option value="3months">{t('orgAdmin.organizationSettings.cadenceOptions.3months')}</option>
                <option value="1month">{t('orgAdmin.organizationSettings.cadenceOptions.1month')}</option>
              </select>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                {t('orgAdmin.organizationSettings.defaultCadenceHelp')}
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                {t('orgAdmin.organizationSettings.timezone')}
              </label>
              <select
                value={settings.timezone}
                onChange={(e) => setSettings(prev => ({ ...prev, timezone: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={loading}
              >
                <option value="UTC">{t('orgAdmin.organizationSettings.timezoneOptions.UTC')}</option>
                <option value="America/New_York">{t('orgAdmin.organizationSettings.timezoneOptions.America/New_York')}</option>
                <option value="America/Chicago">{t('orgAdmin.organizationSettings.timezoneOptions.America/Chicago')}</option>
                <option value="America/Denver">{t('orgAdmin.organizationSettings.timezoneOptions.America/Denver')}</option>
                <option value="America/Los_Angeles">{t('orgAdmin.organizationSettings.timezoneOptions.America/Los_Angeles')}</option>
                <option value="Europe/London">{t('orgAdmin.organizationSettings.timezoneOptions.Europe/London')}</option>
                <option value="Europe/Paris">{t('orgAdmin.organizationSettings.timezoneOptions.Europe/Paris')}</option>
                <option value="Europe/Berlin">{t('orgAdmin.organizationSettings.timezoneOptions.Europe/Berlin')}</option>
                <option value="Asia/Tokyo">{t('orgAdmin.organizationSettings.timezoneOptions.Asia/Tokyo')}</option>
                <option value="Asia/Shanghai">{t('orgAdmin.organizationSettings.timezoneOptions.Asia/Shanghai')}</option>
              </select>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end border-t border-slate-200 dark:border-slate-700 pt-6">
          <button
            onClick={handleSave}
            disabled={loading}
            className="flex items-center px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
          >
            {loading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            {t('orgAdmin.organizationSettings.saveChanges')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrgSettings; 